import React from "react";
import { useNavigate } from "react-router-dom";
import ContinueButton from "../Buttons/ContinueButton";

export interface DictionaryEmptyStateProps {
  emoji: string;
  title: string;
  description: string;
  ctaLabel?: string;
  onCta?: () => void;
}

/**
 * DictionaryEmptyState — shared empty state for the 4 distinct dictionary
 * lists (words/phrases × learning/known). Ports the iOS Empty*State components.
 */
const DictionaryEmptyState: React.FC<DictionaryEmptyStateProps> = ({
  emoji,
  title,
  description,
  ctaLabel = "Go to Feed",
  onCta,
}) => {
  const nav = useNavigate();
  // Tabs live under /home/* (HomeLayout); a bare "/feed" matches no route.
  const handleCta = onCta ?? (() => nav("/home/feed"));

  return (
    <div className="flex flex-col items-center justify-center w-full text-center px-8 py-12">
      <div className="text-5xl mb-4">{emoji}</div>
      <div className="text-lg font-semibold text-[var(--color-text-primary)]">
        {title}
      </div>
      <div className="text-base text-[var(--color-grey)] mb-6 mt-1">
        {description}
      </div>
      <ContinueButton
        text={ctaLabel}
        onClick={handleCta}
        className="max-w-[260px]"
      />
    </div>
  );
};

// iOS ref: Empty{Learning,Known}{,Phrases}State.tsx — exact copy/emoji.
export const EMPTY_STATES = {
  "words-learning": {
    emoji: "👆",
    title: "Click on words while watching",
    description: "Tap any word in a lesson to add it here for review",
  },
  "words-known": {
    emoji: "✓",
    title: "Mark words you already know",
    description:
      'Tap "I know this" so Parrot can find the perfect content for your level',
  },
  "phrases-learning": {
    emoji: "👆",
    title: "Highlight phrases while watching",
    description: "Select multiple words to save useful phrases for review",
  },
  "phrases-known": {
    emoji: "✓",
    title: "Mark phrases you already know",
    description:
      'Tap "I know this" so Parrot can find the perfect content for your level',
  },
} as const;

export type EmptyStateKey = keyof typeof EMPTY_STATES;

export default DictionaryEmptyState;
